import { useState, KeyboardEvent } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Send } from "lucide-react";
import { LinaChatModal } from "./LinaChatModal";
import type { DashboardBriefing, SuggestionAction } from "@shared/dashboard";
import linaImage from "@assets/lina desk_1764231656647.png";

// The single Lina banner. Replaces HeroLinaBanner, HeroLinaChat and
// AgentGreetingBanner (three near-duplicates). Dashboard passes the server
// briefing; Team.tsx renders the plain greeting variant without one.

interface LinaBriefingHeroProps {
  companyId: string;
  userId: string;
  userName?: string;
  /** Server-assembled briefing; undefined = loading, null = greeting-only variant */
  briefing?: DashboardBriefing | null;
  onSuggestion?: (action: SuggestionAction) => void;
  /** The company query finished and there is none — chat can never enable until the profile exists */
  profileMissing?: boolean;
}

export function LinaBriefingHero({
  companyId,
  userId,
  userName,
  briefing,
  onSuggestion,
  profileMissing,
}: LinaBriefingHeroProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [initialMessage, setInitialMessage] = useState<string | undefined>();

  const chatDisabled = !companyId || !userId;

  const handleOpenChat = (message?: string) => {
    if (chatDisabled) return;
    setInitialMessage(message);
    setIsChatOpen(true);
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleOpenChat(inputValue.trim());
    }
  };

  const fallbackGreeting = userName?.trim()
    ? `Schön, dass Sie wieder da sind, ${userName.trim()}! Ich habe schon ein paar Strategie-Ideen für Sie.`
    : "Schön, dass Sie wieder da sind! Ich habe schon ein paar Strategie-Ideen für Sie.";

  return (
    <>
      <Card className="mb-6 overflow-visible" data-testid="card-lina-briefing">
        <CardContent className="py-4 px-4">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden shadow-md border-2 border-purple-200 dark:border-purple-800">
                <img src={linaImage} alt="Lina - Marketing Strategist" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                LINA
              </div>
            </div>

            <div className="flex-1 min-w-0 w-full sm:w-auto text-center sm:text-left">
              {briefing === undefined ? (
                <div className="space-y-2 mb-3" data-testid="briefing-loading">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              ) : (
                <p className="text-sm sm:text-base text-foreground mb-3" data-testid="text-briefing">
                  {briefing?.text ?? fallbackGreeting}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                {briefing?.suggestion && onSuggestion && (
                  <Button
                    onClick={() => onSuggestion(briefing.suggestion!.action)}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="button-briefing-suggestion"
                  >
                    {briefing.suggestion.label}
                    <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                  </Button>
                )}
                <div className="flex flex-1 gap-2">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ihre Frage an Lina"
                    className="flex-1 text-sm"
                    disabled={chatDisabled}
                    data-testid="input-lina-question"
                  />
                  <Button
                    onClick={() => (inputValue.trim() ? handleOpenChat(inputValue.trim()) : handleOpenChat())}
                    className="bg-purple-600"
                    size="icon"
                    disabled={chatDisabled}
                    aria-label="Frage an Lina senden"
                    data-testid="button-send-question"
                  >
                    <Send className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              {chatDisabled && (
                <p className="text-xs text-muted-foreground mt-2">
                  {profileMissing
                    ? "Lina ist startklar, sobald Sie Ihr Firmenprofil angelegt haben. Schließen Sie dazu Ihren Check-In ab."
                    : "Lina ist verfügbar, sobald Ihr Firmenprofil geladen ist."}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <LinaChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        initialMessage={initialMessage}
        companyId={companyId}
        userId={userId}
        userName={userName}
      />
    </>
  );
}
